/**
 * Token store implementations.
 *
 * - `MemoryTokenStore` — in-memory; the default and what unit tests use.
 * - `createLocalStorageTokenStore` — persists the token pair under a single key
 *   in `localStorage`, so a dashboard reload keeps the session. Falls back to an
 *   in-memory store if `localStorage` is unavailable (SSR / privacy mode).
 */
import type { TokenPair } from '@hotline/contracts';

import type { TokenStore } from './types.js';

/** Default storage key for the persisted token pair. */
export const DEFAULT_TOKEN_STORAGE_KEY = 'hotline.auth.tokens';

/** Simple in-memory token store (default; used by tests). */
export class MemoryTokenStore implements TokenStore {
  private tokens: TokenPair | null = null;

  constructor(initial: TokenPair | null = null) {
    this.tokens = initial;
  }

  get(): TokenPair | null {
    return this.tokens;
  }

  set(tokens: TokenPair | null): void {
    this.tokens = tokens;
  }

  clear(): void {
    this.tokens = null;
  }
}

/** Minimal `Storage`-like surface so we can inject a fake in tests. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Build a token store backed by a `Storage` (defaults to `window.localStorage`).
 * Reads/writes a single JSON-encoded key. Any access error degrades gracefully
 * to memory so the app never crashes over storage availability.
 */
export function createLocalStorageTokenStore(options?: {
  storage?: StorageLike | null;
  key?: string;
}): TokenStore {
  const key = options?.key ?? DEFAULT_TOKEN_STORAGE_KEY;
  // An explicit `storage` (including `null`) is honored as-is; only when the
  // option is omitted do we fall back to the ambient `window.localStorage`.
  const storage =
    options && 'storage' in options
      ? options.storage
      : typeof globalThis !== 'undefined' &&
          (globalThis as { localStorage?: StorageLike }).localStorage
        ? (globalThis as { localStorage: StorageLike }).localStorage
        : null;

  if (!storage) {
    return new MemoryTokenStore();
  }

  return {
    get(): TokenPair | null {
      try {
        const raw = storage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw) as TokenPair;
      } catch {
        return null;
      }
    },
    set(tokens: TokenPair | null): void {
      try {
        if (tokens === null) {
          storage.removeItem(key);
        } else {
          storage.setItem(key, JSON.stringify(tokens));
        }
      } catch {
        /* ignore quota / availability errors */
      }
    },
    clear(): void {
      try {
        storage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };
}
